import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../../utils/fetchInterceptor';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

interface Category {
  _id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  projectCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/highcode/categories`
      );

      if (!response.ok) throw new Error("Failed to fetch categories");

      const data = await response.json();
      setCategories(data.categories || data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching categories:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleCategoryStatus = async (id: string) => {
    try {
      setIsUpdating(true);
      const category = categories.find(c => c._id === id);
      if (!category) return;

      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/categories/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isActive: !category.isActive }),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to update category status");

      await fetchCategories(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update category status");
      console.error("Error updating category status:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteCategory = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category? This will affect all associated projects.')) return;

    try {
      setIsDeleting(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/categories/${id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to delete category");

      await fetchCategories(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete category");
      console.error("Error deleting category:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const updateCategory = async (updatedCategory: Category) => {
    try {
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/categories/${updatedCategory._id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedCategory),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to update category");

      await fetchCategories(); // Refresh the list
      setEditingCategory(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update category");
      console.error("Error updating category:", err);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Categories</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchCategories}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Categories Management</h1>
          <p className="text-gray-600">Manage project categories (LEDs, Games, Robotics, etc.)</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <i className="fas fa-plus mr-2"></i>
          Add New Category
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search Categories</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search categories..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.map((category) => (
          <div key={category._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              {/* Category Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: category.color }}
                  >
                    <i className={`fas fa-${category.icon} text-xl`}></i>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                    <p className="text-sm text-gray-500">{category.projectCount} projects</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleCategoryStatus(category._id)}
                  disabled={isUpdating}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    category.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
                >
                  {category.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>

              {/* Category Description */}
              <p className="text-gray-600 text-sm mb-4">{category.description}</p>

              {/* Color Preview */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">Color</label>
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{ backgroundColor: category.color }}
                  ></div>
                  <span className="text-xs text-gray-600">{category.color}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button 
                  onClick={() => setEditingCategory(category)}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors"
                >
                  <i className="fas fa-edit mr-1"></i>
                  Edit
                </button>
                <button className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md text-sm hover:bg-green-700 transition-colors">
                  <i className="fas fa-eye mr-1"></i>
                  View
                </button>
                <button 
                  onClick={() => deleteCategory(category._id)}
                  disabled={isDeleting}
                  className={`bg-red-600 text-white px-3 py-2 rounded-md text-sm hover:bg-red-700 transition-colors ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-tags text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Categories</p>
              <p className="text-2xl font-semibold text-gray-900">{categories.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-check-circle text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Categories</p>
              <p className="text-2xl font-semibold text-gray-900">
                {categories.filter(c => c.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-code text-2xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Projects</p>
              <p className="text-2xl font-semibold text-gray-900">
                {categories.reduce((sum, cat) => sum + cat.projectCount, 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-chart-bar text-2xl text-orange-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg. Projects/Category</p>
              <p className="text-2xl font-semibold text-gray-900">
                {Math.round(categories.reduce((sum, cat) => sum + cat.projectCount, 0) / categories.length)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Category</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              updateCategory(editingCategory);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editingCategory.description}
                    onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="color"
                    value={editingCategory.color}
                    onChange={(e) => setEditingCategory({...editingCategory, color: e.target.value})}
                    className="w-full h-10 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingCategory(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
